import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  Companies,
  CompaniesListParam,
  CompanyPayload,
  CreateApiResult,
  HookParams,
} from '@/libs/interfaces'
import { companiesApi } from '@/services/api'

export function useCompanyByID({
  params,
  enabled,
}: HookParams<{ orgID: string; companyOrgNumber: string }>) {
  return useQuery<Companies>({
    queryKey: QUERY_KEYS.companies.id(params.companyOrgNumber),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await companiesApi.getByID(params)
    },
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: companiesApi.update,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companies.id(variables.companyOrgNumber ?? ''),
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companies.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useCreateCompanies() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation<CreateApiResult<Companies>, Error, CompanyPayload>({
    mutationFn: companiesApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.companies.all,
      })
      toast.success(getCreatedToastMessage(result, t('companies.companyAdded')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useCompanies({ params, enabled }: HookParams<CompaniesListParam>) {
  return useQuery<Companies[]>({
    queryKey: QUERY_KEYS.companies.list(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await companiesApi.list(params)
    },
    staleTime: 30 * 60 * 1000, //30 mins
  })
}

export function useInfiniteCompanies({ params, enabled }: HookParams<CompaniesListParam>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.companies.infinite(params),
    queryFn: ({ pageParam }) =>
      companiesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}
